import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Plus, MagnifyingGlass, Copy, DotsThreeVertical } from "@phosphor-icons/react";
import { toast } from "sonner";

import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import TablePagination from "@/components/TablePagination";
import { api } from "@/lib/api";
import { ADMIN } from "@/lib/routes";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtDelta = (v) => {
  const n = Number(v || 0);
  const abs = fmt$(Math.abs(n));
  if (n < 0) return `-${abs}`;
  if (n > 0) return `+${abs}`;
  return abs;
};

export default function Merchants() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [balanceModal, setBalanceModal] = useState(null);
  const [balanceAmt, setBalanceAmt] = useState("");
  const [balanceNote, setBalanceNote] = useState("");
  const [balanceSubmitting, setBalanceSubmitting] = useState(false);
  const balanceLock = useRef(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const createLock = useRef(false);
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", webhook_url: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (q) params.q = q;
      const r = await api.get("/admin/merchants", { params });
      setItems(r.data.items);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } finally {
      setLoading(false);
    }
  }, [q, page, limit]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, limit]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (createLock.current || createSubmitting) return;
    createLock.current = true;
    setCreateSubmitting(true);
    try {
      const payload = {
        company_name: form.company_name,
        email: form.email,
        webhook_url: form.webhook_url || undefined,
      };
      const contact = form.contact_name.trim();
      if (contact) payload.contact_name = contact;
      await api.post("/admin/merchants", payload);
      toast.success("Merchant created");
      setCreating(false);
      setForm({ company_name: "", contact_name: "", email: "", webhook_url: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      createLock.current = false;
      setCreateSubmitting(false);
    }
  };

  const closeCreateModal = () => {
    if (createSubmitting) return;
    setCreating(false);
  };

  const onApprove = async (m) => {
    await api.patch(`/admin/merchants/${m.id}`, { kyb_status: "approved", status: "active" });
    toast.success("KYB approved");
    load();
  };
  const onSuspend = async (m) => {
    await api.patch(`/admin/merchants/${m.id}`, { status: m.status === "suspended" ? "active" : "suspended" });
    load();
  };
  const onRotate = async (m) => {
    const r = await api.post(`/admin/merchants/${m.id}/generate-api-key`);
    setRevealedKey({ merchant: m.company_name, key: r.data.api_key, masked: r.data.masked });
    load();
  };

  const openBalanceModal = (merchant, mode) => {
    setBalanceModal({ merchant, mode });
    setBalanceAmt("");
    setBalanceNote("");
  };

  const onBalanceSubmit = async (e) => {
    e.preventDefault();
    if (balanceLock.current || balanceSubmitting || !balanceModal) return;
    const amount = Number(balanceAmt);
    if (!amount || amount <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    const { merchant, mode } = balanceModal;
    const path = mode === "debit" ? "debit-balance" : "fund-balance";
    balanceLock.current = true;
    setBalanceSubmitting(true);
    try {
      await api.post(`/admin/merchants/${merchant.id}/${path}`, {
        amount,
        note: balanceNote.trim() || undefined,
      });
      toast.success(
        mode === "debit"
          ? `Debited ${fmt$(amount)} from ${merchant.company_name}`
          : `Funded ${fmt$(amount)} to ${merchant.company_name}`,
      );
      setBalanceModal(null);
      setBalanceAmt("");
      setBalanceNote("");
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404) {
        toast.error("Balance API not found — restart backend-node (npm run dev).");
      } else {
        toast.error(detail || err?.message || `Failed to ${mode} merchant`);
      }
    } finally {
      balanceLock.current = false;
      setBalanceSubmitting(false);
    }
  };

  const closeBalanceModal = () => {
    if (balanceSubmitting) return;
    setBalanceModal(null);
    setBalanceAmt("");
    setBalanceNote("");
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="merchants-page">
        <PageHeader
          title="Merchants"
          subtitle="Onboard, gate, and govern every B2B partner."
          actions={
            <button
              onClick={() => setCreating(true)}
              data-testid="create-merchant-btn"
              className="h-9 px-4 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150"
            >
              <Plus size={14} weight="bold" /> New merchant
            </button>
          }
        />

        <Surface className="p-3 mb-4" testId="merchant-search">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="merchant-search-input"
              className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none"
              placeholder="Search by name or email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />
          </div>
        </Surface>

        <Surface testId="merchants-table-card">
          <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">Company</th>
                <th className="px-5 py-3">ID</th>
                <th className="px-5 py-3">KYB</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Balance</th>
                <th className="px-5 py-3">API Key</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-zinc-500">Loading…</td></tr>
              )}
              {!loading && items.map((m) => (
                <tr key={m.id} data-testid={`merchant-row-${m.id}`} className="border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors duration-150">
                  <td className="px-5 py-3">
                    <Link to={ADMIN.merchant(m.id)} className="font-medium text-zinc-950 hover:underline">
                      {m.company_name}
                    </Link>
                    <div className="text-xs text-zinc-500">{m.email}</div>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-zinc-600">{m.id.slice(0, 8)}…</td>
                  <td className="px-5 py-3"><StatusPill value={m.kyb_status} /></td>
                  <td className="px-5 py-3"><StatusPill value={m.status} /></td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(m.balance)}</td>
                  <td className="px-5 py-3 font-mono text-xs text-zinc-600">{m.api_key_masked || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <MerchantActionsMenu
                      merchant={m}
                      open={openMenuId === m.id}
                      onOpenChange={(next) => setOpenMenuId(next ? m.id : null)}
                      onApprove={() => { setOpenMenuId(null); onApprove(m); }}
                      onFund={() => { setOpenMenuId(null); openBalanceModal(m, "fund"); }}
                      onDebit={() => { setOpenMenuId(null); openBalanceModal(m, "debit"); }}
                      onRotate={() => { setOpenMenuId(null); onRotate(m); }}
                      onSuspend={() => { setOpenMenuId(null); onSuspend(m); }}
                    />
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-zinc-500">No merchants yet.</td></tr>
              )}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            loading={loading}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </Surface>

        {creating && (
          <Modal title="Onboard merchant" onClose={closeCreateModal}>
            <form onSubmit={onCreate} className="space-y-3">
              <Field label="Company name" testId="new-merchant-company" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} disabled={createSubmitting} />
              <Field label="Contact name (optional)" testId="new-merchant-contact" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} disabled={createSubmitting} />
              <div>
                <Field label="Login email" type="email" testId="new-merchant-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} disabled={createSubmitting} />
                <p className="mt-1 text-xs text-zinc-500">Merchant portal sign-in OTP is sent to this address.</p>
              </div>
              <Field label="Webhook URL (optional)" testId="new-merchant-webhook" value={form.webhook_url} onChange={(v) => setForm({ ...form, webhook_url: v })} disabled={createSubmitting} />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" disabled={createSubmitting} onClick={closeCreateModal} className="h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150 disabled:opacity-50 disabled:pointer-events-none">Cancel</button>
                <button type="submit" disabled={createSubmitting} data-testid="submit-new-merchant" className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 transition-colors duration-150 disabled:opacity-50 disabled:pointer-events-none">{createSubmitting ? "Creating…" : "Create"}</button>
              </div>
            </form>
          </Modal>
        )}

        {balanceModal && (
          <Modal
            title={`${balanceModal.mode === "debit" ? "Debit" : "Fund"} ${balanceModal.merchant.company_name}`}
            onClose={closeBalanceModal}
          >
            <form onSubmit={onBalanceSubmit} className="space-y-3">
              <p className="text-sm text-zinc-600">
                Current balance:{" "}
                <span className="font-mono tabular-nums font-medium text-zinc-950">
                  {fmt$(balanceModal.merchant.balance)}
                </span>
              </p>
              {balanceModal.mode === "debit" && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                  Debits reduce the merchant wallet used for card fees and loads. Fails if balance is too low.
                </p>
              )}
              <Field
                label="Amount (USD)"
                type="number"
                testId="balance-amount-input"
                value={balanceAmt}
                onChange={setBalanceAmt}
                disabled={balanceSubmitting}
              />
              <Field
                label="Note (optional)"
                testId="balance-note-input"
                value={balanceNote}
                onChange={setBalanceNote}
                disabled={balanceSubmitting}
              />
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  disabled={balanceSubmitting}
                  onClick={closeBalanceModal}
                  className="h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150 disabled:opacity-50 disabled:pointer-events-none"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={balanceSubmitting}
                  data-testid="submit-balance-btn"
                  className={`h-9 px-4 text-sm text-white rounded-sm transition-colors duration-150 disabled:opacity-50 disabled:pointer-events-none ${
                    balanceModal.mode === "debit"
                      ? "bg-red-700 hover:bg-red-800"
                      : "bg-zinc-950 hover:bg-zinc-800"
                  }`}
                >
                  {balanceSubmitting
                    ? balanceModal.mode === "debit"
                      ? "Debiting…"
                      : "Funding…"
                    : balanceModal.mode === "debit"
                      ? "Debit balance"
                      : "Fund balance"}
                </button>
              </div>
            </form>
          </Modal>
        )}

        {revealedKey && (
          <Modal title="API key generated" onClose={() => setRevealedKey(null)}>
            <p className="text-sm text-zinc-600 mb-3">
              This is the only time the full key for <strong>{revealedKey.merchant}</strong> will be displayed.
              Store it in a secure vault before closing this dialog.
            </p>
            <div className="bg-zinc-950 text-emerald-300 font-mono text-xs p-4 rounded-sm break-all" data-testid="revealed-api-key">
              {revealedKey.key}
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(revealedKey.key); toast.success("Copied"); }}
              data-testid="copy-api-key-btn"
              className="mt-3 inline-flex items-center gap-2 h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150"
            >
              <Copy size={14} /> Copy to clipboard
            </button>
          </Modal>
        )}
      </div>
    </Layout>
  );
}

function MerchantActionsMenu({ merchant: m, open, onOpenChange, onApprove, onFund, onDebit, onRotate, onSuspend }) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const placeMenu = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 176;
    const menuHeight = 200;
    const gap = 4;
    let top = rect.bottom + gap;
    let left = rect.right - menuWidth;
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeight - gap);
    }
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    setMenuStyle({ position: "fixed", top, left, width: menuWidth, zIndex: 50 });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    placeMenu();
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [open, placeMenu]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      onOpenChange(false);
    };
    const onKey = (e) => { if (e.key === "Escape") onOpenChange(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="Merchant actions"
        aria-expanded={open}
        data-testid={`merchant-actions-${m.id}`}
        onClick={() => onOpenChange(!open)}
        className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 transition-colors duration-150"
      >
        <DotsThreeVertical size={18} weight="bold" />
      </button>
      {open && menuStyle && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={menuStyle}
          className="min-w-[11rem] rounded-sm border border-zinc-200 bg-white py-1 shadow-lg"
          data-testid={`merchant-actions-menu-${m.id}`}
        >
          <MenuItem to={ADMIN.merchant(m.id)} onClick={() => onOpenChange(false)}>View details</MenuItem>
          <MenuItem onClick={onFund} testId={`fund-${m.id}`}>Fund merchant</MenuItem>
          <MenuItem onClick={onDebit} testId={`debit-${m.id}`} destructive>Debit merchant</MenuItem>
          {m.kyb_status !== "approved" && (
            <MenuItem onClick={onApprove} testId={`approve-kyb-${m.id}`}>Approve KYB</MenuItem>
          )}
          <MenuItem onClick={onRotate} testId={`rotate-key-${m.id}`}>Rotate key</MenuItem>
          <MenuItem onClick={onSuspend} testId={`suspend-${m.id}`} destructive={m.status !== "suspended"}>
            {m.status === "suspended" ? "Activate" : "Suspend"}
          </MenuItem>
        </div>,
        document.body,
      )}
    </>
  );
}

function MenuItem({ children, onClick, to, testId, destructive }) {
  const cls = `block w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 transition-colors duration-150 ${
    destructive ? "text-red-700" : "text-zinc-900"
  }`;
  if (to) {
    return (
      <Link to={to} role="menuitem" data-testid={testId} className={cls} onClick={onClick}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" role="menuitem" data-testid={testId} className={cls} onClick={onClick}>
      {children}
    </button>
  );
}

export function Field({ label, value, onChange, type = "text", testId, disabled = false, min, hint }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        min={min}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none disabled:bg-zinc-50 disabled:text-zinc-500"
      />
      {hint ? <p className="mt-1 text-xs text-zinc-500">{hint}</p> : null}
    </div>
  );
}

export function Modal({ title, children, onClose }) {
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/40"
      onClick={onClose}
      data-testid="modal-overlay"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="bg-white border border-zinc-200 rounded-sm w-full max-w-md shadow-xl max-h-[min(90vh,100%)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-zinc-100 shrink-0">
          <div id="modal-title" className="font-display text-lg tracking-tight font-medium">{title}</div>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

